
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import mockPaginationData from './demo/mock-pagination-data.js';
import template from './pagination.twig';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Pagination" mdxType="Meta" />
    <h1>{`Pagination`}</h1>
    <p>{`Displays a horizontal list of page number links, including extra affordances for those representing the "previous" and "next" pages.`}</p>
    <p>{`The component supports variable amounts of page numbers and will adjust its layout accordingly. For best results on small screens, we recommend a maximum link count of 5.`}</p>
    <p>{`As the viewport widens, individual numbers may drift awkwardly far apart. You can prevent this by wrapping the component in `}<a parentName="p" {...{
        "href": "/docs/objects-container--basic"
      }}>{`a container`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Example" args={{
        current: 2,
        midSize: 2,
        total: 36
      }} argTypes={{
        current: {
          type: {
            name: 'number'
          }
        },
        midSize: {
          type: {
            name: 'number'
          }
        },
        total: {
          type: {
            name: 'number'
          }
        }
      }} mdxType="Story">
    {args => template({
          pagination: mockPaginationData(args)
        })}
  </Story>
    </Canvas>
    <h2>{`Elements`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`c-pagination`}</inlineCode>{`: A containing `}<inlineCode parentName="li">{`<nav>`}</inlineCode>{` element.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`c-pagination__items`}</inlineCode>{`: A `}<inlineCode parentName="li">{`<ul>`}</inlineCode>{` containing the page links.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`c-pagination__item`}</inlineCode>{`: An individual list item.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`c-pagination__action`}</inlineCode>{`: The link (or possibly in the future, button) that navigates to the page.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`c-pagination__number`}</inlineCode>{`: Wrapper for the page number within `}<inlineCode parentName="li">{`c-pagination__action`}</inlineCode>{`.`}</li>
    </ul>
    <h2>{`Action States`}</h2>
    <p>{`In addition to the usual hover, active and focus states, `}<inlineCode parentName="p">{`c-pagination__action`}</inlineCode>{` supports three more:`}</p>
    <ul>
      <li parentName="ul">{`An `}<inlineCode parentName="li">{`aria-current`}</inlineCode>{` attribute (typically with a value of `}<inlineCode parentName="li">{`page`}</inlineCode>{`) to identify the current page action.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`is-previous`}</inlineCode>{` or `}<inlineCode parentName="li">{`is-next`}</inlineCode>{` classes to identify the `}<em parentName="li">{`previous`}</em>{` or `}<em parentName="li">{`next`}</em>{` page action.`}</li>
    </ul>
    <h2>{`Accessibility`}</h2>
    <p>{`Like all navigation patterns, pagination is potentially fraught with accessibility issues. Here's what we've done to make this component more inclusive:`}</p>
    <ul>
      <li parentName="ul">{`The outer `}<inlineCode parentName="li">{`<nav>`}</inlineCode>{` element clearly identifies this component as navigation to assistive devices.`}</li>
      <li parentName="ul">{`Said element includes an `}<inlineCode parentName="li">{`aria-labelledby`}</inlineCode>{` attribute, which maps to the ID of a `}<a parentName="li" {...{
          "href": "/docs/utilities-display--hidden-visually"
        }}>{`visually hidden`}</a>{` heading element within. We use `}<inlineCode parentName="li">{`aria-labelledby`}</inlineCode>{` instead of `}<inlineCode parentName="li">{`aria-label`}</inlineCode>{` to improve `}<a parentName="li" {...{
          "href": "https://heydonworks.com/article/aria-label-is-a-xenophobe/"
        }}>{`compatibility with translation tools`}</a>{`.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`<ul>`}</inlineCode>{` element containing list items includes a `}<inlineCode parentName="li">{`role="list"`}</inlineCode>{` attribute to avoid `}<a parentName="li" {...{
          "href": "https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html"
        }}>{`an issue in Safari/VoiceOver`}</a>{`.`}</li>
      <li parentName="ul">{`All page actions include `}<a parentName="li" {...{
          "href": "https://www.a11ymatters.com/pattern/pagination/#2--labeling-the-navigation-links"
        }}>{`visually hidden text for context`}</a>{` ("Page 4" instead of simply "4").`}</li>
      <li parentName="ul">{`The current page action is identified using `}<a parentName="li" {...{
          "href": "https://tink.uk/using-the-aria-current-attribute/"
        }}>{`the `}<inlineCode parentName="a">{`aria-current`}</inlineCode>{` attribute`}</a>{`.`}</li>
      <li parentName="ul">{`It is common to output multiple links for "previous" and "next" for styling purposes, but we've avoided that so no page number is read aloud multiple times.`}</li>
    </ul>
    <h2>{`Template Properties`}</h2>
    <p>{`The Twig template expects to receive a `}<inlineCode parentName="p">{`pagination`}</inlineCode>{` object with the same structure as `}<a parentName="p" {...{
        "href": "https://timber.github.io/docs/guides/pagination/"
      }}><inlineCode parentName="a">{`posts.pagination`}</inlineCode>{` in Timber`}</a>{`, streamlining integration with WordPress projects that use that plugin.`}</p>
    <p>{`It also introduces properties to control the accessible label:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`label_id`}</inlineCode>{`: ID for the accessible label. Defaults to `}<inlineCode parentName="li">{`pagination-label`}</inlineCode>{`. Must be unique per page.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`label_tag_name`}</inlineCode>{`: Tag for the accessible label. Defaults to `}<inlineCode parentName="li">{`h2`}</inlineCode>{`, but should be adjusted depending on current document outline.`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const example = (args => template({
  pagination: mockPaginationData(args)
}));
example.storyName = 'Example';
example.argTypes = {
  current: {
    type: {
      name: 'number'
    }
  },
  midSize: {
    type: {
      name: 'number'
    }
  },
  total: {
    type: {
      name: 'number'
    }
  }
};
example.args = {
  current: 2,
  midSize: 2,
  total: 36
};
example.parameters = { storySource: { source: 'args => template({\n  pagination: mockPaginationData(args)\n})' } };

const componentMeta = { title: 'Components/Pagination', includeStories: ["example"],  };

const mdxStoryNameToKey = {"Example":"example"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;